import { makeApi, Zodios, type ZodiosOptions } from '@zodios/core';
import { z } from 'zod';

const Region = z
  .object({ code: z.string().nullable(), name: z.string().nullable() })
  .partial()
  .passthrough();
const Country = z.object({ code2: z.string(), code3: z.string(), name: z.string() }).passthrough();
const Confidence = z.enum(['EXACT', 'HIGH', 'PARTIAL', 'LOW']);
const AddressResponse = z
  .object({
    label: z.string(),
    addressNumber: z.string().nullish(),
    streetName: z.string().nullish(),
    streetType: z.string().nullish(),
    street: z.string().nullish(),
    locality: z.string().nullish(),
    district: z.string().nullish(),
    region: Region.nullish(),
    postalCode: z.string().nullish(),
    country: Country,
    secondaryAddress: z.string().nullish(),
    confidence: Confidence,
  })
  .passthrough();
const ErrorResponse = z
  .object({
    error: z.enum(['INVALID_REQUEST', 'NOT_FOUND', 'THROTTLED', 'INTERNAL_ERROR']),
    message: z.string(),
  })
  .passthrough();
const Suggestion = z.object({ label: z.string(), placeId: z.string() }).passthrough();
const AutocompleteResponse = z.object({ suggestions: z.array(Suggestion) }).passthrough();
const PlaceResponse = z
  .object({
    label: z.string(),
    addressNumber: z.string().nullish(),
    streetName: z.string().nullish(),
    streetType: z.string().nullish(),
    street: z.string().nullish(),
    locality: z.string().nullish(),
    district: z.string().nullish(),
    region: Region.nullish(),
    postalCode: z.string().nullish(),
    country: Country,
    secondaryAddress: z.string().nullish(),
  })
  .passthrough();

export const schemas = {
  Region,
  Country,
  Confidence,
  AddressResponse,
  ErrorResponse,
  Suggestion,
  AutocompleteResponse,
  PlaceResponse,
};

const endpoints = makeApi([
  {
    method: 'get',
    path: '/v1/autocomplete',
    alias: 'autocompleteAddress',
    description: `Returns a list of address suggestions for a partial text query. Designed
for frontend typeahead; each suggestion carries a &#x60;label&#x60; and &#x60;placeId&#x60;
which can be passed to &#x60;GET /v1/places/{placeId}&#x60; to retrieve the full
structured address.

- Returns **200** with a &#x60;suggestions&#x60; array, possibly empty.
- Rejects any query parameters not listed below with a **400** error.
`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'query',
        type: 'Query',
        schema: z.string().min(3).max(200),
      },
      {
        name: 'countryHint',
        type: 'Query',
        schema: z
          .string()
          .regex(/^[A-Za-z]{2}$/)
          .optional(),
      },
      {
        name: 'maxResults',
        type: 'Query',
        schema: z.number().int().gte(1).lte(10).optional().default(5),
      },
    ],
    response: AutocompleteResponse,
    errors: [
      {
        status: 400,
        description: `Invalid request.`,
        schema: ErrorResponse,
      },
      {
        status: 429,
        description: `Rate limit exceeded.`,
        schema: ErrorResponse,
      },
      {
        status: 500,
        description: `Unexpected server error.`,
        schema: ErrorResponse,
      },
    ],
  },
  {
    method: 'get',
    path: '/v1/places/:placeId',
    alias: 'getPlaceById',
    description: `Returns the full structured address for a &#x60;placeId&#x60; previously obtained
from &#x60;GET /v1/autocomplete&#x60;. No confidence score is returned because the
lookup is deterministic.

- Returns **200** with the address.
- Returns **404** if the &#x60;placeId&#x60; is unknown or has expired.
`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'placeId',
        type: 'Path',
        schema: z.string().max(500),
      },
    ],
    response: PlaceResponse,
    errors: [
      {
        status: 400,
        description: `Missing or invalid placeId.`,
        schema: ErrorResponse,
      },
      {
        status: 404,
        description: `placeId not found.`,
        schema: ErrorResponse,
      },
      {
        status: 429,
        description: `Rate limit exceeded.`,
        schema: ErrorResponse,
      },
      {
        status: 500,
        description: `Unexpected server error.`,
        schema: ErrorResponse,
      },
    ],
  },
  {
    method: 'get',
    path: '/v1/resolve',
    alias: 'resolveAddress',
    description: `Geocodes a free-text address string and returns a structured address with
confidence scoring.

- Returns **200** with the best match when a result meets the requested
  confidence threshold.
- Returns **204** when no match is found or the best match falls below
  &#x60;minConfidence&#x60;.
- Rejects any query parameters not listed below with a **400** error.
`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'address',
        type: 'Query',
        schema: z.string().min(1),
      },
      {
        name: 'minConfidence',
        type: 'Query',
        schema: z.enum(['EXACT', 'HIGH', 'PARTIAL', 'LOW']).optional(),
      },
      {
        name: 'countryHint',
        type: 'Query',
        schema: z.string().optional(),
      },
    ],
    response: AddressResponse,
    errors: [
      {
        status: 400,
        description: `Invalid request. Possible causes:
- &#x60;address&#x60; parameter is missing or blank.
- &#x60;minConfidence&#x60; value is not one of &#x60;EXACT&#x60;, &#x60;HIGH&#x60;, &#x60;PARTIAL&#x60;, &#x60;LOW&#x60;.
- One or more unrecognised query parameters were supplied.
`,
        schema: ErrorResponse,
      },
      {
        status: 429,
        description: `Rate limit exceeded. The API enforces a limit of 10 requests/second
(burst 20) and a monthly quota of 10,000 requests.
`,
        schema: ErrorResponse,
      },
      {
        status: 500,
        description: `Unexpected server error.`,
        schema: ErrorResponse,
      },
    ],
  },
]);

export const api = new Zodios(endpoints);

export function createApiClient(baseUrl: string, options?: ZodiosOptions) {
  return new Zodios(baseUrl, endpoints, options);
}
